import { useMemo } from "react";
import type { MapRef } from "react-map-gl/mapbox";
import { useMap } from "react-map-gl/mapbox";
import { selectedItemTypeSchema, useSelection } from "./SelectionContext";
import type { INonRoutableEmployeePerformedActivityOccurrenceOrGroup } from "@medoma/models/activities";
import type { IVisit } from "@medoma/models/visits";
import type { IRoute } from "@medoma/models/routes";
import { useCareHub } from "@/api/Patients";
import { getVisitsCoordinates } from "@/api/Routes";
import { usePlanningViewToolbar } from "../usePlanningViewToolbar";

const fitMapToRoute = ({
  map,
  careHub,
  visits,
}: {
  map: MapRef;
  careHub: { longitude: number; latitude: number };
  visits: IRoute["visits"];
}) => {
  const coordinates = getVisitsCoordinates(visits);
  if (coordinates.length === 0) {
    return;
  }
  const lngs = [
    careHub.longitude,
    ...coordinates.map(({ longitude }) => longitude),
  ];
  const lats = [
    careHub.latitude,
    ...coordinates.map(({ latitude }) => latitude),
  ];
  map.fitBounds(
    [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ],
    { padding: 80, duration: 500 },
  );
};

export const useSelectionInTimeline = () => {
  const { selectedItem, setSelectedItem, clearSelection } = useSelection();
  const { isMapShown, setMapShown } = usePlanningViewToolbar();
  const { planningMap } = useMap();
  const { data: careHub } = useCareHub();

  // Check if a specific activity occurrence is selected
  const isActivityOccurrenceOrGroupSelected = (
    occurrenceOrGroup: INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
  ) =>
    selectedItem &&
    selectedItem.type ===
      selectedItemTypeSchema.enum.plannedActivityOccurrenceOrGroup &&
    selectedItem.item.id === occurrenceOrGroup.id;

  // Check if a specific visit is selected
  const isVisitSelected = (visit: { id: string }) =>
    selectedItem &&
    selectedItem.type === selectedItemTypeSchema.enum.plannedVisit &&
    selectedItem.item.id === visit.id;

  // Check if a specific route is selected
  const isRouteSelected = (routeId: string) =>
    selectedItem &&
    selectedItem.type === selectedItemTypeSchema.enum.plannedRoute &&
    selectedItem.routeId === routeId;

  // Toggle activity occurrence selection
  const toggleActivityOccurrenceSelection = (
    occurrenceOrGroup: INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
    shiftId: number,
  ) => {
    if (isActivityOccurrenceOrGroupSelected(occurrenceOrGroup)) {
      clearSelection();
    } else {
      setSelectedItem({
        item: occurrenceOrGroup,
        shiftId,
        type: selectedItemTypeSchema.enum.plannedActivityOccurrenceOrGroup,
      });
    }
  };

  // Toggle visit selection
  const toggleVisitSelection = (
    visit: IVisit,
    routeId: string,
    shiftId: number,
  ) => {
    if (isVisitSelected(visit)) {
      clearSelection();
    } else {
      setSelectedItem({
        type: selectedItemTypeSchema.enum.plannedVisit,
        item: visit,
        routeId,
        shiftId,
      });
    }
  };

  // Toggle route selection
  const toggleRouteSelection = (
    route: Pick<IRoute, "id" | "visits">,
    shiftId: number,
  ) => {
    if (isRouteSelected(route.id)) {
      clearSelection();
      return;
    }
    setSelectedItem({
      routeId: route.id,
      type: selectedItemTypeSchema.enum.plannedRoute,
      shiftId,
    });
    if (!isMapShown) {
      setMapShown(true);
    }
    // When the map is already mounted we zoom here. If the click just opened the
    // map, PlanningMap picks up the selected route via its initialAreaToShow.
    if (planningMap && careHub) {
      fitMapToRoute({ map: planningMap, careHub, visits: route.visits });
    }
  };

  // Compute selected route ID
  const selectedRouteId = useMemo(() => {
    if (!selectedItem) {
      return null;
    }

    if (
      selectedItem.type === selectedItemTypeSchema.enum.plannedRoute ||
      selectedItem.type === selectedItemTypeSchema.enum.plannedVisit
    ) {
      return selectedItem.routeId;
    }

    return null;
  }, [selectedItem]);

  return {
    isActivityOccurrenceOrGroupSelected,
    isVisitSelected,
    isRouteSelected,
    toggleActivityOccurrenceSelection,
    toggleVisitSelection,
    toggleRouteSelection,
    selectedRouteId,
  };
};
